import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const FAQSection = () => {
  const [openFAQ, setOpenFAQ] = useState(0);
  const [copiedEmail, setCopiedEmail] = useState('');

  const handleCopy = async (email) => {
    try {
      await navigator.clipboard?.writeText(email);
      setCopiedEmail(email);
      setTimeout(() => setCopiedEmail(''), 2000);
    } catch (err) {
      console.error('Copy failed', err);
      setCopiedEmail('');
    }
  };

  const faqs = [
    {
      id: 1,
      question: "What's your typical response time?",
      answer: `I aim to respond to all inquiries within 24 hours, often much sooner. For urgent matters marked as "URGENT" in the subject line, I typically respond within 2-4 hours during business hours. Weekend responses may take up to 48 hours unless it's a critical issue.`
    },
    {
      id: 2,
      question: "Do you work with international clients?",
      answer: `Absolutely! I have extensive experience working with clients across different time zones including Europe, Asia, and Australia. I'm flexible with meeting times and use various communication tools to ensure smooth collaboration regardless of location.`
    },
    {
      id: 3,
      question: "What information should I include in my project inquiry?",
      answer: `Please include: project scope and objectives, target audience, preferred technology stack, timeline and budget range, any existing assets or requirements, and your preferred communication method. The more details you provide, the better I can understand your needs and provide an accurate proposal.`
    },
    {
      id: 4,
      question: "Do you offer free consultations?",
      answer: `Yes! I offer a complimentary 30-minute consultation call to discuss your project, understand your requirements, and explore how we can work together. This helps both of us determine if we're a good fit before moving forward with any formal agreement.`
    },
    {
      id: 5,
      question: "What types of projects do you typically work on?",
      answer: `I specialize in web applications, mobile apps, API development, and technical consulting. My expertise includes React, Node.js, Python, and cloud technologies. I work on everything from startup MVPs to enterprise applications, with a focus on scalable, maintainable solutions.`
    },
    {
      id: 6,
      question: "How do you handle project communication and updates?",
      answer: `I believe in transparent, regular communication. I provide weekly progress reports, maintain shared project documentation, and am available for quick check-ins via Slack or email. For larger projects, I set up dedicated communication channels and regular milestone reviews.`
    },
    {
      id: 7,
      question: "What's your availability for new projects?",
      answer: `My availability varies depending on current commitments. I typically book 2-4 weeks in advance for new projects. For urgent projects, I may be able to accommodate rush timelines with adjusted pricing. I'll always be upfront about my availability during our initial consultation.`
    },
    {
      id: 8,
      question: "Do you provide ongoing support after project completion?",
      answer: `Yes! I offer various support packages including bug fixes, feature updates, performance monitoring, and technical maintenance. I believe in building long-term relationships with clients and am always available to help your project evolve and grow.`
    }
  ];

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? -1 : index);
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">Frequently Asked Questions</h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">Get quick answers to common questions about working together, project processes, and collaboration details.</p>
        </div>

        {/* FAQ List */}
        <div className="space-y-4">
          {faqs?.map((faq, index) => (
            <div key={faq?.id} className="bg-slate-50 rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-lg">
              <button onClick={() => toggleFAQ(index)} className="w-full px-8 py-6 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset">
                <h3 className="text-lg font-semibold text-slate-900 pr-4">{faq?.question}</h3>
                <div className={`flex-shrink-0 transition-transform duration-300 ${openFAQ === index ? 'rotate-180' : ''}`}>
                  <Icon name="ChevronDown" size={24} className="text-slate-500" />
                </div>
              </button>
              
              <div className={`transition-all duration-300 ease-in-out ${openFAQ === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
                <div className="px-8 pb-6">
                  <div className="border-t border-slate-200 pt-6">
                    <p className="text-slate-600 leading-relaxed whitespace-pre-line">{faq?.answer}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Still Have Questions */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-8 text-white">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="HelpCircle" size={32} color="white" />
            </div>
            <h3 className="text-2xl font-bold mb-4">Still Have Questions?</h3>
            <p className="text-blue-100 mb-8 text-lg max-w-2xl mx-auto">Don't see your question answered here? Choose the email that matches how formal your inquiry is and I'll get back within the promised window.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
              <div className="bg-white/10 rounded-xl p-6 border border-white/20 backdrop-blur-sm">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-sm uppercase tracking-wide text-blue-100">Personal Email</p>
                    <h4 className="text-xl font-semibold text-white">Within 12 hours</h4>
                  </div>
                  <Icon name="Clock" size={22} className="text-white/80" />
                </div>
                <p className="text-blue-100 mb-4">Direct personal email for casual inquiries and quick questions.</p>
                <div className="flex items-center justify-between bg-white/10 border border-white/20 rounded-lg px-3 py-2">
                  <span className="text-white font-semibold">info@mustan.dev</span>
                  <button onClick={() => handleCopy('info@mustan.dev')} className="text-sm text-white/80 hover:text-white">
                    {copiedEmail === 'info@mustan.dev' ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <ul className="mt-4 space-y-1 text-blue-50 text-sm">
                  <li>• Quick Questions</li>
                  <li>• Casual Inquiries</li>
                  <li>• Follow-ups</li>
                </ul>
                <button
                  type="button"
                  onClick={() => window.location.href = 'mailto:info@mustan.dev'}
                  className="mt-5 inline-flex items-center justify-center w-full px-4 py-3 bg-white text-blue-700 font-semibold rounded-lg hover:bg-blue-50 transition-colors"
                >
                  <Icon name="Mail" size={18} className="mr-2" />
                  Send Email
                </button>
              </div>

              <div className="bg-white/10 rounded-xl p-6 border border-white/20 backdrop-blur-sm">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <p className="text-sm uppercase tracking-wide text-blue-100">Direct Email</p>
                    <h4 className="text-xl font-semibold text-white">Within 24 hours</h4>
                  </div>
                  <Icon name="Mail" size={22} className="text-white/80" />
                </div>
                <p className="text-blue-100 mb-4">For formal inquiries, job opportunities, and detailed project discussions.</p>
                <div className="flex items-center justify-between bg-white/10 border border-white/20 rounded-lg px-3 py-2">
                  <span className="text-white font-semibold">info@mustan.dev</span>
                  <button onClick={() => handleCopy('info@mustan.dev')} className="text-sm text-white/80 hover:text-white">
                    {copiedEmail === 'info@mustan.dev' ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <ul className="mt-4 space-y-1 text-blue-50 text-sm">
                  <li>• Job Opportunities</li>
                  <li>• Project Proposals</li>
                  <li>• Formal Inquiries</li>
                </ul>
                <button
                  type="button"
                  onClick={() => window.location.href = 'mailto:info@mustan.dev'}
                  className="mt-5 inline-flex items-center justify-center w-full px-4 py-3 bg-white text-blue-700 font-semibold rounded-lg hover:bg-blue-50 transition-colors"
                >
                  <Icon name="Send" size={18} className="mr-2" />
                  Send Email
                </button>
              </div>
            </div>

            <div className="mt-6">
              <a href="#contact-form" className="inline-flex items-center justify-center px-6 py-3 bg-white/20 text-white font-semibold rounded-lg hover:bg-white/30 transition-colors border border-white/30">
                <Icon name="MessageSquare" size={20} className="mr-2" />
                Use Contact Form
              </a>
            </div>
          </div>
        </div>

        {/* Quick Contact Info */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-6 bg-slate-50 rounded-xl">
            <Icon name="Clock" size={32} className="text-blue-600 mx-auto mb-4" />
            <h4 className="font-semibold text-slate-900 mb-2">Quick Response</h4>
            <p className="text-sm text-slate-600">Most emails answered within 4 hours during business days</p>
          </div>
          <div className="text-center p-6 bg-slate-50 rounded-xl">
            <Icon name="Calendar" size={32} className="text-emerald-600 mx-auto mb-4" />
            <h4 className="font-semibold text-slate-900 mb-2">Free Consultation</h4>
            <p className="text-sm text-slate-600">30-minute call to discuss your project at no cost</p>
          </div>
          <div className="text-center p-6 bg-slate-50 rounded-xl">
            <Icon name="Shield" size={32} className="text-purple-600 mx-auto mb-4" />
            <h4 className="font-semibold text-slate-900 mb-2">NDA Available</h4>
            <p className="text-sm text-slate-600">Happy to sign confidentiality agreements for sensitive projects</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
